import { useState, type FC, type ChangeEvent, type DragEvent } from 'react';
import { getPresignedUrl, uploadFileToS3, saveMediaMetadata, type GetPresignedUrlRequest, type SaveMediaMetadataRequest } from '../../shared/api/media';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './MediaUpload.css';

/**
 * 選択されたファイル情報
 */
interface SelectedFileInfo {
  file: File;
  filename: string;
  uploadProgress: number;
  uploadedUrl: string | null;
  error: string | null;
  isUploading: boolean;
}

/**
 * ファイルアップロード画面コンポーネント
 */
const MediaUpload: FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileInfo[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const { logout } = useAuth();
  const adminLoginPath = getAdminLoginPath();

  /**
   * メディアタイプを判定する
   */
  const getMediaType = (contentType: string): 'image' | 'video' | null => {
    if (contentType.startsWith('image/')) {
      return 'image';
    } else if (contentType.startsWith('video/')) {
      return 'video';
    }
    return null;
  };

  /**
   * ファイル名から拡張子を取得する
   */
  const getFileExtension = (fileName: string): string => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.substring(lastDot) : '';
  };

  /**
   * 画像ファイルからEXIFデータ（位置情報）を削除する
   * Canvas APIを使用して画像を再描画することで、EXIFデータを含まない新しいファイルを生成
   */
  const removeExifData = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Canvasを作成して画像を描画
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas contextの取得に失敗しました'));
            return;
          }
          
          // 画像をCanvasに描画（EXIFデータは含まれない）
          ctx.drawImage(img, 0, 0);
          
          // CanvasからBlobとして取得
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('画像の変換に失敗しました'));
                return;
              }
              
              // 元のファイル名と拡張子を保持した新しいFileオブジェクトを作成
              const newFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              
              resolve(newFile);
            },
            file.type, // 元のMIMEタイプを保持
            0.95 // 品質（JPEGの場合）
          );
        };
        
        img.onerror = () => {
          reject(new Error('画像の読み込みに失敗しました'));
        };
        
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => {
        reject(new Error('ファイルの読み込みに失敗しました'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  /**
   * ファイル選択時の処理（複数ファイル対応）
   * 画像ファイルの場合はEXIFデータ（位置情報）を削除してから処理
   */
  const handleFilesSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: SelectedFileInfo[] = [];
    const errors: string[] = [];

    // 各ファイルを順次処理（非同期処理のためfor...ofを使用）
    for (const file of fileArray) {
      try {
        // メディアタイプを判定
        const mediaType = getMediaType(file.type);
        if (!mediaType) {
          errors.push(`${file.name}: サポートされていないファイル形式です。画像（JPEG, PNG, GIF, WebP）または動画（MP4）を選択してください。`);
          continue;
        }

        // 画像ファイルの場合はEXIFデータ（位置情報）を削除
        let processedFile = file;
        if (mediaType === 'image') {
          try {
            processedFile = await removeExifData(file);
          } catch (err) {
            errors.push(`${file.name}: 画像の処理に失敗しました。${err instanceof Error ? err.message : '不明なエラー'}`);
            continue;
          }
        }

        // ファイルサイズチェック（仕様に合わせて画像5MB、動画500MB）
        // EXIFデータ削除後のサイズでチェック
        const maxSizeImage = 5 * 1024 * 1024; // 5MB
        const maxSizeVideo = 500 * 1024 * 1024; // 500MB
        const maxSize = mediaType === 'image' ? maxSizeImage : maxSizeVideo;
        const maxSizeMB = mediaType === 'image' ? 5 : 500;
        
        if (processedFile.size > maxSize) {
          errors.push(`${file.name}: ファイルサイズが大きすぎます。${mediaType === 'image' ? '画像' : '動画'}は${maxSizeMB}MB以下を選択してください。`);
          continue;
        }

        // 既に同じファイルが選択されているかチェック
        // EXIFデータ削除後のファイルでチェック
        const isDuplicate = selectedFiles.some(f => f.file.name === processedFile.name && f.file.size === processedFile.size && f.file.lastModified === processedFile.lastModified);
        if (isDuplicate) {
          errors.push(`${file.name}: 既に選択されているファイルです。`);
          continue;
        }

        validFiles.push({
          file: processedFile,
          filename: file.name, // 元のファイル名を保持
          uploadProgress: 0,
          uploadedUrl: null,
          error: null,
          isUploading: false,
        });
      } catch (err) {
        errors.push(`${file.name}: ファイルの処理に失敗しました。${err instanceof Error ? err.message : '不明なエラー'}`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setSuccessMessage(null);
    }
  };

  /**
   * ファイル入力の変更処理（複数ファイル対応）
   */
  const handleFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFilesSelect(files);
    }
  };

  /**
   * ドラッグ&ドロップの処理
   */
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFilesSelect(files);
    }
  };

  /**
   * ファイル名のバリデーション
   */
  const validateFilename = (name: string): string | null => {
    if (!name || name.trim() === '') {
      return 'ファイル名を入力してください';
    }
    
    // ファイル名に使用できない文字をチェック
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(name)) {
      return 'ファイル名に使用できない文字が含まれています';
    }
    
    // ファイル名の長さチェック（255文字制限）
    if (name.length > 255) {
      return 'ファイル名が長すぎます（最大255文字）';
    }
    
    return null;
  };

  /**
   * ファイル名を更新
   */
  const handleFilenameChange = (index: number, newFilename: string) => {
    setSelectedFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, filename: newFilename } : item
    ));
  };

  /**
   * ファイルを削除
   */
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  /**
   * 単一ファイルのアップロード処理
   */
  const uploadSingleFile = async (fileInfo: SelectedFileInfo, index: number): Promise<string> => {
    const { file, filename } = fileInfo;

    // ファイル名のバリデーション
    const filenameError = validateFilename(filename);
    if (filenameError) {
      throw new Error(filenameError);
    }

    // メディアタイプを判定
    const mediaType = getMediaType(file.type);
    if (!mediaType) {
      throw new Error('サポートされていないファイル形式です。');
    }

    // ファイル名に拡張子が含まれていない場合、元のファイル名から拡張子を取得
    const originalExtension = getFileExtension(file.name);
    const finalFilename = filename.includes('.') ? filename : filename + originalExtension;

    // 進捗更新: Presigned URL取得開始
    setSelectedFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, uploadProgress: 10, isUploading: true, error: null } : item
    ));

    // 1. Presigned URLを取得
    const contentType = file.type || 'application/octet-stream';
    const request: GetPresignedUrlRequest = {
      filename: finalFilename,
      file_size: file.size,
      content_type: contentType,
      media_type: mediaType,
    };

    const presignedResponse = await getPresignedUrl(request);

    // 進捗更新: S3アップロード開始
    setSelectedFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, uploadProgress: 30 } : item
    ));

    // 2. S3にファイルをアップロード
    if (presignedResponse.upload_type === 'single') {
      await uploadFileToS3(
        presignedResponse.presigned_url,
        file,
        contentType
      );
      setSelectedFiles(prev => prev.map((item, i) => 
        i === index ? { ...item, uploadProgress: 70 } : item
      ));
    } else {
      throw new Error('マルチパートアップロードは現在サポートされていません。');
    }

    // 3. メタデータを保存
    const metadataRequest: SaveMediaMetadataRequest = {
      media_id: presignedResponse.media_id,
      s3_key: presignedResponse.s3_key,
      filename: finalFilename,
      file_size: file.size,
      content_type: file.type || 'application/octet-stream',
      media_type: mediaType,
    };

    const metadataResponse = await saveMediaMetadata(metadataRequest);

    // 進捗更新: 完了
    const uploadedUrl = metadataResponse.cloudfront_url || metadataResponse.s3_url;
    setSelectedFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, uploadProgress: 100, uploadedUrl, isUploading: false } : item
    ));

    return uploadedUrl;
  };

  /**
   * 複数ファイルの一括アップロード処理
   */
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('ファイルが選択されていません');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccessMessage(null);

    const uploadedCount = { count: 0 };
    const errors: string[] = [];

    try {
      // 各ファイルを順次アップロード
      for (let i = 0; i < selectedFiles.length; i++) {
        try {
          await uploadSingleFile(selectedFiles[i], i);
          uploadedCount.count++;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'アップロードに失敗しました';
          errors.push(`${selectedFiles[i].file.name}: ${errorMessage}`);
          setSelectedFiles(prev => prev.map((item, idx) => 
            idx === i ? { ...item, error: errorMessage, isUploading: false } : item
          ));
        }
      }

      // 結果メッセージを表示
      if (uploadedCount.count > 0) {
        setSuccessMessage(`${uploadedCount.count}件のファイルのアップロードが完了しました`);
      }
      if (errors.length > 0) {
        setError(errors.join('\n'));
      }
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        window.location.href = adminLoginPath;
        return;
      }
      
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('ファイルのアップロードに失敗しました');
      }
    } finally {
      setIsUploading(false);
      
      // ファイル入力のリセット
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  /**
   * ファイル選択のリセット
   */
  const handleReset = () => {
    setSelectedFiles([]);
    setError(null);
    setSuccessMessage(null);
    
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * ファイルURLをクリップボードにコピー（記事作成画面と同じ形式でMarkdownテキストを生成）
   */
  const handleCopyUrl = async (url: string, filename: string) => {
    if (!url) {
      return;
    }

    // 記事作成画面と同じ形式でMarkdownテキストを生成
    const imageMarkdown = `![${filename}](${url})`;

    try {
      await navigator.clipboard.writeText(imageMarkdown);
      setSuccessMessage('✓ 画像タグをクリップボードにコピーしました');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setToastError('✗ 画像タグのコピーに失敗しました');
      setTimeout(() => {
        setToastError(null);
      }, 3000);
    }
  };

  return (
    <div className="media-upload-container">
      <AdminSidebar />
      <main className="media-upload-main">
        <div className="media-upload-content">
          <div className="media-upload-header">
            <h1>メディアアップロード</h1>
          </div>

          {error && (
            <div className="media-upload-error-inline">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="media-upload-success">
              {successMessage}
            </div>
          )}

          {toastError && (
            <div className="media-upload-error">
              {toastError}
            </div>
          )}

          <div className="media-upload-form">
            {/* ファイル選択エリア */}
            <div className="form-group">
              <label htmlFor="file-input">ファイルを選択（複数選択可能）</label>
              <div
                className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${selectedFiles.length > 0 ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept="image/*,video/mp4"
                  onChange={handleFileInputChange}
                  disabled={isUploading}
                  className="file-input"
                />
                <div className="file-drop-zone-content">
                  {selectedFiles.length > 0 ? (
                    <div className="selected-files-count">
                      {selectedFiles.length}件のファイルが選択されています
                    </div>
                  ) : (
                    <div className="file-drop-zone-placeholder">
                      <div className="upload-icon">📤</div>
                      <p>ファイルをドラッグ&ドロップするか、クリックして選択</p>
                      <p className="file-drop-zone-hint">画像: 最大5MB、動画: 最大500MBまでアップロード可能（複数選択可）</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 選択されたファイル一覧 */}
            {selectedFiles.length > 0 && (
              <div className="selected-files-list">
                {selectedFiles.map((fileInfo, index) => (
                  <div key={index} className="selected-file-item">
                    <div className="selected-file-header">
                      <div className="selected-file-info">
                        <div className="file-icon">📄</div>
                        <div className="file-details">
                          <div className="file-name">{fileInfo.file.name}</div>
                          <div className="file-size">
                            {(fileInfo.file.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                          <div className="file-type">{fileInfo.file.type || '不明なタイプ'}</div>
                        </div>
                      </div>
                      {!isUploading && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="btn-remove-file"
                          title="削除"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {/* ファイル名編集 */}
                    <div className="form-group">
                      <label htmlFor={`filename-input-${index}`}>ファイル名</label>
                      <input
                        id={`filename-input-${index}`}
                        type="text"
                        value={fileInfo.filename}
                        onChange={(e) => handleFilenameChange(index, e.target.value)}
                        disabled={isUploading || fileInfo.isUploading}
                        className="filename-input"
                        placeholder="ファイル名を入力"
                      />
                      <small className="filename-hint">
                        拡張子は自動的に保持されます。元のファイル名: {fileInfo.file.name}
                      </small>
                    </div>

                    {/* アップロード進捗 */}
                    {fileInfo.isUploading && (
                      <div className="upload-progress">
                        <div className="upload-progress-bar">
                          <div
                            className="upload-progress-fill"
                            style={{ width: `${fileInfo.uploadProgress}%` }}
                          />
                        </div>
                        <div className="upload-progress-text">
                          {fileInfo.uploadProgress < 30 ? 'Presigned URLを取得中...' : 
                           fileInfo.uploadProgress < 100 ? 'ファイルをアップロード中...' : 
                           'アップロード完了'}
                        </div>
                      </div>
                    )}

                    {/* エラーメッセージ */}
                    {fileInfo.error && (
                      <div className="file-error">
                        {fileInfo.error}
                      </div>
                    )}

                    {/* アップロード済みファイルURL */}
                    {fileInfo.uploadedUrl && (
                      <div className="uploaded-file-url">
                        <div className="form-group">
                          <label>アップロード済みファイルURL</label>
                          <div className="url-display">
                            <input
                              type="text"
                              value={fileInfo.uploadedUrl}
                              readOnly
                              className="url-input"
                            />
                            <button
                              type="button"
                              onClick={() => handleCopyUrl(fileInfo.uploadedUrl!, fileInfo.filename)}
                              className="btn-copy-url"
                            >
                              コピー
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* アクションボタン */}
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                disabled={isUploading || selectedFiles.length === 0}
                className="btn-reset"
              >
                リセット
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={isUploading || selectedFiles.length === 0}
                className="btn-upload"
              >
                {isUploading ? 'アップロード中...' : `アップロード (${selectedFiles.length}件)`}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MediaUpload;

